// INMONEX API module — связь фронтенда с бэкендом
// Автоматический fallback на localStorage при недоступности сервера

// BASE_URL: определяется автоматически
// - Если задан REACT_APP_API_URL — используем его
// - На продакшне (порт 443/80): используем /api (Nginx проксирует на бэкенд)
// - В dev-режиме (другие порты): используем hostname:5000/api
const isProduction = window.location.protocol === 'https:' || window.location.port === '' || window.location.port === '80' || window.location.port === '443';
const BASE_URL = process.env.REACT_APP_API_URL
  || (isProduction ? '/api' : `http://${window.location.hostname}:5000/api`);

console.log('%c[INMONEX API] BASE_URL = ' + BASE_URL, 'color: #00bcd4; font-weight: bold');

const TOKEN_KEY = 'inmonex_token';
const REFRESH_KEY = 'inmonex_refresh_token';
const USER_KEY = 'inmonex_user';
// Stage 11 UI: ID текущей сессии (RefreshToken.id) — приходит с /login, /register,
// /refresh, /2fa/verify. Используется для пометки "Текущая" в "Мои устройства"
// и блокировки кнопки revoke на собственной сессии.
const SESSION_KEY = 'inmonex_session_id';

// ─── Хранилище токенов ───────────────────────────────────

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
function getRefreshToken() { return localStorage.getItem(REFRESH_KEY); }
function setRefreshToken(t) { localStorage.setItem(REFRESH_KEY, t); }

export function getSessionId() { return localStorage.getItem(SESSION_KEY); }
function setSessionId(id) {
  if (id) localStorage.setItem(SESSION_KEY, id);
  else localStorage.removeItem(SESSION_KEY);
}

function saveUser(u) { localStorage.setItem(USER_KEY, JSON.stringify(u)); }
function getUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
}

function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SESSION_KEY);
}

// ─── Флаг доступности сервера ────────────────────────────

let _serverAvailable = null; // null = не проверяли, true/false
let _lastCheck = 0;
const CHECK_INTERVAL = 30000; // перепроверять каждые 30с

async function isServerAvailable() {
  const now = Date.now();
  if (_serverAvailable !== null && now - _lastCheck < CHECK_INTERVAL) {
    return _serverAvailable;
  }
  try {
    const r = await fetch(BASE_URL + '/health', { method: 'GET', signal: AbortSignal.timeout(3000) });
    _serverAvailable = r.ok;
  } catch {
    _serverAvailable = false;
  }
  _lastCheck = now;
  return _serverAvailable;
}

// Принудительно сбросить кэш при успешном запросе
function markServerAvailable() { _serverAvailable = true; _lastCheck = Date.now(); }
function markServerUnavailable() { _serverAvailable = false; _lastCheck = Date.now(); }

// ─── Базовый fetch с авторизацией и refresh ──────────────

let _refreshing = null; // промис текущего refresh (чтобы не дублировать)

async function refreshAccessToken() {
  const rt = getRefreshToken();
  if (!rt) { clearAuth(); return false; }

  try {
    const r = await fetch(BASE_URL + '/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!r.ok) { clearAuth(); return false; }
    const data = await r.json();
    setToken(data.accessToken);
    setRefreshToken(data.refreshToken);
    if (data.sessionId) setSessionId(data.sessionId);
    if (data.user) saveUser(data.user);
    return true;
  } catch {
    return false;
  }
}

// Stage 20 follow-up (2026-05-14): Item 1 — 409 saveOrg косметика.
// Backend point-mutations (POST/PUT/DELETE /operations, /accounts, /funds, etc.)
// touch'ают Org.updatedAt. Если saveOrg PUT /sync/org летит parallel — backend
// видит mismatch expectedSyncedAt → 409. Resolve работает, но console спамит.
// Tracker: timestamp последней WRITE операции на non-bulk endpoint.
// App.js использует в 409 handler чтобы demote warn → debug для race-with-own-op.
let _lastRestWriteAt = 0;
export function getLastRestWriteAt() { return _lastRestWriteAt; }

async function apiFetch(path, options = {}) {
  const url = BASE_URL + path;
  const token = getToken();

  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = 'Bearer ' + token;

  // Bump tracker для всех write операций кроме самой saveOrg PUT (/api/sync/org/...).
  // Bulk PUT — это и есть тот PUT который может получить 409; не считаем его за "недавнюю
  // REST операцию" чтобы не подавлять реальные warn'ы.
  const method = options.method || 'GET';
  if ((method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH')
      && !path.startsWith('/api/sync/org')) {
    _lastRestWriteAt = Date.now();
  }

  console.log(`[API] ${method} ${url}`);

  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (err) {
    console.error(`[API] FETCH FAILED ${url}:`, err.message);
    markServerUnavailable();
    throw new ApiError('Сервер недоступен', 0, true);
  }

  markServerAvailable();

  // Авто-refresh при 401
  if (res.status === 401) {
    // Проверяем — может пользователь удалён из БД ИЛИ токен инвалидирован force-logout-ом
    // (admin сменил пароль / sub-user-у, или юзер сам сменил пароль на другом устройстве).
    // В обоих случаях — мгновенный logout без попытки refresh: refresh всё равно
    // отозван на сервере, попытка refresh лишь даст ещё одно 401.
    try {
      const body401 = await res.clone().json();
      if (body401.code === 'USER_NOT_FOUND' || body401.code === 'TOKEN_INVALIDATED') {
        logout();
        window.location.reload();
        throw new ApiError(body401.error || 'Сессия завершена. Войдите заново.', 401, false);
      }
    } catch (e) { if (e instanceof ApiError) throw e; }

    if (!_refreshing) _refreshing = refreshAccessToken().finally(() => { _refreshing = null; });
    const ok = await _refreshing;
    if (ok) {
      // Повторить запрос с новым токеном
      headers['Authorization'] = 'Bearer ' + getToken();
      try {
        res = await fetch(url, { ...options, headers });
      } catch (err) {
        markServerUnavailable();
        throw new ApiError('Сервер недоступен', 0, true);
      }
    }
  }

  if (!res.ok) {
    let body;
    try { body = await res.json(); } catch { body = {}; }
    const err = new ApiError(body.error || `Ошибка ${res.status}`, res.status, false);
    err.code = body.code || null;
    err.body = body; // Phase 10: для 409 нужен body.current с актуальными данными
    throw err;
  }

  return res.json();
}

// ─── Класс ошибки API ───────────────────────────────────

class ApiError extends Error {
  constructor(message, status, isOffline) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.isOffline = isOffline;
  }
}

// ─── Хелпер: запрос с fallback на localStorage ──────────

async function withFallback(apiCall, fallbackFn) {
  try {
    const available = await isServerAvailable();
    if (!available && fallbackFn) return fallbackFn();
    return await apiCall();
  } catch (err) {
    if (err.isOffline && fallbackFn) return fallbackFn();
    throw err;
  }
}

// ═══════════════════════════════════════════════════════════
// AUTH
// ═══════════════════════════════════════════════════════════

export async function register(login, password, name, email, phone, referredCode, emailVerificationToken) {
  const body = { login, password, name, email };
  if (phone) body.phone = phone;
  if (referredCode) body.referredCode = referredCode;
  // Stage 11: бэк требует токен подтверждения email при наличии email в body.
  // Без токена /register вернёт 400 (см. server/routes/auth.js).
  if (emailVerificationToken) body.emailVerificationToken = emailVerificationToken;
  const data = await apiFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  setToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  if (data.sessionId) setSessionId(data.sessionId);
  saveUser(data.user);
  return data.user;
}

// Регистрация пользователя БЕЗ смены текущей сессии (для добавления сотрудников)
export async function registerUser(login, password, name) {
  const url = BASE_URL + '/auth/register';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login, password, name }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Ошибка регистрации');
    err.status = res.status;
    throw err;
  }
  // НЕ сохраняем токен — оставляем текущую сессию владельца
  return data.user;
}

export async function login(login, password) {
  const data = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ login, password }),
  });
  // Stage 11: если у юзера включён 2FA, бэкенд возвращает tempToken и НЕ выдаёт
  // access/refresh. UI должен показать второй экран (ввод 6-значного кода)
  // и вызвать verify2FA(tempToken, code). Никакие токены сейчас не сохраняем —
  // вход будет завершён только после успешной верификации.
  if (data && data.needs2FA && data.tempToken) {
    return { needs2FA: true, tempToken: data.tempToken };
  }
  setToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  if (data.sessionId) setSessionId(data.sessionId);
  saveUser(data.user);
  return data.user;
}

// ═══ 2FA (Stage 11 UI) ═══
// Backend flow:
//   1. setupInit2FA() → возвращает {secret, otpauthUrl} (НЕ сохраняет в БД)
//   2. setupConfirm2FA(secret, code) → если код валиден, активирует 2FA и
//      возвращает 10 plaintext backup-кодов (показать ОДИН раз!)
//   3. login → если 2FA активна, вернёт {needs2FA, tempToken}
//   4. verify2FA(tempToken, code) → завершает вход, сохраняет токены
//   5. disable2FA(password, code) → отключает 2FA (требует и пароль и код)

// Шаг 1 включения. Не сохраняет ничего на сервере — secret держим в state UI.
export async function setupInit2FA() {
  return apiFetch('/auth/2fa/setup-init', { method: 'POST' });
}

// Шаг 2 включения. После успеха в БД сохранён totpSecret + 10 хешей backup-кодов.
// В ответе backupCodes — plaintext, юзер должен сохранить/распечатать их сейчас же.
// Этап 3.1: бэк теперь требует currentPassword (re-auth) — закрытие уязвимости
// "атакующий с украденным access-токеном включает 2FA жертве".
export async function setupConfirm2FA(secret, code, currentPassword) {
  return apiFetch('/auth/2fa/setup-confirm', {
    method: 'POST',
    body: JSON.stringify({ secret, code, currentPassword }),
  });
}

// Отключение 2FA. Требует и пароль, и код (TOTP или один из backup-кодов).
// Защищает от XSS/угнанной сессии: украденный access-токен сам по себе
// не позволит отключить 2FA без знания пароля.
export async function disable2FA(password, code) {
  return apiFetch('/auth/2fa/disable', {
    method: 'POST',
    body: JSON.stringify({ password, code }),
  });
}

// Шаг 2 логина (для юзеров с включённой 2FA). tempToken приходит из login().
// На успехе сохраняет access/refresh/sessionId и возвращает user.
// Бэкенд также возвращает {usedBackup, backupCodesRemaining} — UI может
// предупредить юзера если кодов осталось мало.
export async function verify2FA(tempToken, code) {
  const data = await apiFetch('/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify({ tempToken, code }),
  });
  setToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  if (data.sessionId) setSessionId(data.sessionId);
  saveUser(data.user);
  return data;
}

// Статус 2FA текущего юзера для UI настроек: {enabled, enabledAt, backupCodesRemaining}.
export async function status2FA() {
  return apiFetch('/auth/2fa/status');
}

export function logout() {
  clearAuth();
}

// ═══ SESSIONS / "Мои устройства" (Stage 11 UI) ═══
// Список активных сессий текущего юзера. Возвращает {sessions: [{id, createdAt, expiresAt, ip, userAgent}]}.
export async function getSessions() {
  return apiFetch('/auth/sessions');
}

// Завершить конкретную сессию по ID. Owner-check на бэке.
// Если ID = текущей сессии — следующий /refresh завершится 401, фронт почистит auth.
export async function revokeSession(id) {
  return apiFetch('/auth/sessions/' + encodeURIComponent(id), {
    method: 'DELETE',
  });
}

// Завершить ВСЕ сессии (включая текущую). После вызова auth локально почистится.
export async function logoutAll() {
  return apiFetch('/auth/logout-all', { method: 'POST' });
}

export { getUser, getToken, isServerAvailable, ApiError };

// ═══════════════════════════════════════════════════════════
// ORGS
// ═══════════════════════════════════════════════════════════

export async function getOrgs() {
  return apiFetch('/orgs');
}

export async function createOrg(data) {
  return apiFetch('/orgs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOrg(id, data) {
  return apiFetch('/orgs/' + id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOrg(id, password) {
  // Stage 11: бэкенд требует подтверждение паролем для DELETE org
  return apiFetch('/orgs/' + id, {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  });
}

// ═══════════════════════════════════════════════════════════
// USERS (в контексте организации)
// ═══════════════════════════════════════════════════════════

export async function getOrgUsers(orgId) {
  return withFallback(
    () => apiFetch('/orgs/' + orgId + '/users'),
    () => {
      try {
        const meta = JSON.parse(localStorage.getItem('finup_meta') || '{}');
        const org = (meta.orgs || []).find(o => o.id === orgId);
        return org ? (org.users || []) : [];
      } catch { return []; }
    }
  );
}

export async function searchUser(orgId, query) {
  return apiFetch('/orgs/' + orgId + '/users/search?q=' + encodeURIComponent(query));
}

export async function addUserToOrg(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateUserRole(orgId, userId, role, permissions) {
  return apiFetch('/orgs/' + orgId + '/users/' + userId, {
    method: 'PUT',
    body: JSON.stringify({ role, permissions }),
  });
}

export async function removeUser(orgId, userId) {
  return apiFetch('/orgs/' + orgId + '/users/' + userId, { method: 'DELETE' });
}

export async function resetUserPassword(orgId, userId, password) {
  return apiFetch('/orgs/' + orgId + '/users/' + userId + '/password', {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });
}

// Stage 11: смена логина sub-user-а (только owner). Не трогает пароль/2FA/сессии —
// это для UX "опечатка в логине" / "переименование". Бэк проверяет уникальность —
// при коллизии вернёт 409 с code='LOGIN_TAKEN'. Формат — латиница+цифры+._-,
// 3-32 символа, начинается с буквы (см. server/routes/users.js LOGIN_REGEX).
export async function changeUserLogin(orgId, userId, login) {
  return apiFetch('/orgs/' + orgId + '/users/' + userId + '/login', {
    method: 'PUT',
    body: JSON.stringify({ login }),
  });
}

// Stage 11/Этап 2: установить или удалить email sub-user'а (только owner).
// email=null/'' → удалить email. Иначе — установить новый.
// При коллизии бэк вернёт 409 с code='EMAIL_TAKEN'.
// Без OTP — это admin-action; не верифицируется через email-подтверждение.
export async function changeUserEmail(orgId, userId, email) {
  return apiFetch('/orgs/' + orgId + '/users/' + userId + '/email', {
    method: 'PUT',
    body: JSON.stringify({ email: email || null }),
  });
}

export async function getUserOrgs(orgId, userId) {
  return apiFetch('/orgs/' + orgId + '/users/' + userId + '/orgs');
}

export async function toggleUserOrg(orgId, userId, targetOrgId, enabled, role) {
  return apiFetch('/orgs/' + orgId + '/users/' + userId + '/toggle-org', {
    method: 'POST',
    body: JSON.stringify({ targetOrgId, enabled, role }),
  });
}

// ═══════════════════════════════════════════════════════════
// ACCOUNTS
// ═══════════════════════════════════════════════════════════

export async function getAccounts(orgId) {
  return withFallback(
    () => apiFetch('/orgs/' + orgId + '/accounts'),
    () => {
      try {
        const key = 'finup_' + orgId;
        const d = JSON.parse(localStorage.getItem(key) || '{}');
        return d.accounts || [];
      } catch { return []; }
    }
  );
}

export async function createAccount(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAccount(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/accounts/' + id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAccount(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/accounts/' + id, { method: 'DELETE' });
}

// Чанк J (2026-05-19): atomic archive счёта с переносом balance в target.
// Используется AFTab.archAcc/DataTab.archAcc когда у счёта balance != 0.
// Backend в одной Prisma $transaction: target.balance += deleted.balance + archive deleted.
// data: { balanceTarget: accountId | null }
//   null OK если balance=0 (просто archive).
export async function archiveAccountWithTransfer(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/accounts/' + id + '/archive-with-transfer', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════
// OPERATIONS
// ═══════════════════════════════════════════════════════════

export async function getOperations(orgId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.type) params.set('type', filters.type);
  if (filters.accountId) params.set('accountId', filters.accountId);
  if (filters.contractorId) params.set('contractorId', filters.contractorId);
  if (filters.category) params.set('category', filters.category);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));

  const qs = params.toString();
  const path = '/orgs/' + orgId + '/operations' + (qs ? '?' + qs : '');

  return withFallback(
    () => apiFetch(path),
    () => {
      try {
        const key = 'finup_' + orgId;
        const d = JSON.parse(localStorage.getItem(key) || '{}');
        const ops = d.operations || [];
        return { operations: ops, total: ops.length };
      } catch { return { operations: [], total: 0 }; }
    }
  );
}

export async function createOperation(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/operations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOperation(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/operations/' + id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOperation(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/operations/' + id, { method: 'DELETE' });
}

// Stage 20g (2026-06-08): атомарная отмена оплаты долга.
export async function undoDebtPayment(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/operations/' + id + '/undo-debt-payment', { method: 'POST' });
}

// ═══════════════════════════════════════════════════════════
// DEBTS
// ═══════════════════════════════════════════════════════════

export async function getDebts(orgId) {
  return withFallback(
    () => apiFetch('/orgs/' + orgId + '/debts'),
    () => {
      try {
        const key = 'finup_' + orgId;
        const d = JSON.parse(localStorage.getItem(key) || '{}');
        return d.debts || [];
      } catch { return []; }
    }
  );
}

export async function createDebt(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/debts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDebt(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/debts/' + id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteDebt(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/debts/' + id, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════
// CONTRACTORS
// ═══════════════════════════════════════════════════════════

export async function getContractors(orgId) {
  return withFallback(
    () => apiFetch('/orgs/' + orgId + '/contractors'),
    () => {
      try {
        const key = 'finup_' + orgId;
        const d = JSON.parse(localStorage.getItem(key) || '{}');
        return d.contractors || [];
      } catch { return []; }
    }
  );
}

export async function createContractor(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/contractors', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateContractor(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/contractors/' + id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteContractor(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/contractors/' + id, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════
// FUNDS (Stage 20a Phase 2 / Чанк 20a.2 — backend-only, UI пока не использует)
// ═══════════════════════════════════════════════════════════
// ⚠️ ВАЖНО: эти методы пока НЕ вызываются из UI. UI читает фонды из syncData JSON.
// Переключение UI на эти endpoints — отдельный Чанк 20a.4. До этого момента вызов
// мутирующих методов (createFund/updateFund/archiveFund) создаст drift между Fund
// таблицей и syncData.funds JSON, так как bulk PUT /sync/org перезапишет JSON
// и UI потеряет изменение.

export async function getFunds(orgId, opts = {}) {
  const params = new URLSearchParams();
  if (opts.includeArchived) params.set('includeArchived', 'true');
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const path = '/orgs/' + orgId + '/funds' + (qs ? '?' + qs : '');

  return withFallback(
    () => apiFetch(path),
    () => {
      try {
        const key = 'finup_' + orgId;
        const d = JSON.parse(localStorage.getItem(key) || '{}');
        const fundsRaw = Array.isArray(d.funds) ? d.funds : [];
        const balances = (d.fundBalances && typeof d.fundBalances === 'object') ? d.fundBalances : {};
        // Фильтр archived согласован с backend default (includeArchived=false скрывает архивные)
        const includeArchived = !!opts.includeArchived;
        const funds = fundsRaw
          .filter(f => f && (includeArchived || !f.archived))
          .map(f => ({
            id: f.id,
            orgId,
            name: f.name,
            percent: parseFloat(f.percent) || 0,
            balance: parseFloat(balances[f.id]) || 0,
            color: f.color || null,
            isArchived: !!f.archived,
          }));
        return { funds, total: funds.length };
      } catch { return { funds: [], total: 0 }; }
    }
  );
}

export async function getFund(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/funds/' + id);
}

export async function createFund(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/funds', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFund(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/funds/' + id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Soft-delete (sets isArchived=true). Backend гарантирует idempotency: повторный
// вызов на уже архивированный фонд возвращает 200 с текущим состоянием, не ошибку.
export async function archiveFund(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/funds/' + id, { method: 'DELETE' });
}

// Чанк I (2026-05-19): atomic архивация с перераспределением balance + percent.
// Используется в FundManagerModal при удалении фонда у которого balance > 0
// или есть проценты которые нужно перенести на другие фонды.
// Backend в одной Prisma $transaction: increment target funds + archive deleted с balance=0.
// data: { pctTargets: { fundId: percent, ... }, balanceTarget?: fundId }
export async function archiveFundWithRedistribute(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/funds/' + id + '/archive-with-redistribute', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════
// CATEGORIES (Stage 20b Phase 2 / Чанк B.1, 2026-05-14)
// ═══════════════════════════════════════════════════════════

export async function getCategories(orgId, opts = {}) {
  const params = new URLSearchParams();
  if (opts.type) params.append('type', opts.type);
  if (opts.includeArchived) params.append('includeArchived', 'true');
  const qs = params.toString();
  return apiFetch('/orgs/' + orgId + '/categories' + (qs ? '?' + qs : ''));
}

export async function createCategory(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCategory(orgId, id, data) {
  return apiFetch('/orgs/' + orgId + '/categories/' + id, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function archiveCategory(orgId, id) {
  return apiFetch('/orgs/' + orgId + '/categories/' + id, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════
// PROFILE (текущий пользователь)
// ═══════════════════════════════════════════════════════════

export async function getProfile() {
  return withFallback(
    () => apiFetch('/me'),
    () => getUser()
  );
}

export async function deleteMyAccount(password) {
  // Stage 11: бэкенд требует подтверждение паролем для DELETE /me
  return apiFetch('/me', {
    method: 'DELETE',
    body: JSON.stringify({ password }),
  });
}

// Stage 11: история security-событий (для UI "Активность безопасности")
export async function getSecurityEvents() {
  return apiFetch('/me/security-events');
}

export async function updateProfile(data) {
  const user = await apiFetch('/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  saveUser(user);
  return user;
}

// ─── Безопасная смена пароля (Stage 11) ────────────────────────
// Использует /me/change-password — отзывает все остальные сессии и выдаёт
// новые токены текущей сессии (юзер не вылогинивается у себя).
// Возвращает { ok, revokedSessions } для UI-уведомления.
export async function changePassword(currentPassword, newPassword) {
  const res = await apiFetch('/me/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (res?.accessToken) setToken(res.accessToken);
  if (res?.refreshToken) setRefreshToken(res.refreshToken);
  if (res?.sessionId) setSessionId(res.sessionId);
  return { ok: !!res?.ok, revokedSessions: res?.revokedSessions || 0 };
}

// ─── Смена email: двухшаговая с OTP ───────────────────────────
// Stage 11: бэкенд требует currentPassword — защита от email-takeover атаки
// (украденный access-токен без пароля не позволит сменить email и угнать аккаунт).
export async function requestEmailChange(currentPassword, newEmail, lang) {
  return apiFetch('/me/email/request', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newEmail, lang }),
  });
}

export async function confirmEmailChange(code, lang) {
  const res = await apiFetch('/me/email/confirm', {
    method: 'POST',
    body: JSON.stringify({ code, lang }),
  });
  if (res && res.user) saveUser(res.user);
  return res;
}

// ─── Сброс пароля ──────────────────────────────────────────────
export async function forgotPassword(email, lang) {
  return apiFetch('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email, lang }),
  });
}

export async function resetPasswordWithToken(token, newPassword) {
  return apiFetch('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
}

// ═══════════════════════════════════════════════════════════
// AUDIT LOG
// ═══════════════════════════════════════════════════════════

export async function getAuditLog(orgId, opts = {}) {
  // Stage 21.4: opts.shape='ui' возвращает UI-shape entries
  // ({id,uid,uName,uRole,date,type,desc,before,after,details}). Без — DB shape.
  // opts.limit/offset для пагинации.
  const limit = opts.limit != null ? opts.limit : 50;
  const offset = opts.offset != null ? opts.offset : 0;
  const shape = opts.shape ? `&shape=${encodeURIComponent(opts.shape)}` : '';
  return apiFetch('/orgs/' + orgId + '/audit?limit=' + limit + '&offset=' + offset + shape);
}

// Stage 21.5 (2026-05-14): создать audit entry через REST. Frontend addAuditLog
// (shared.js) вызывает это параллельно с uOrg для немедленной персистенции в
// нормализованную AuditLog таблицу. Body: {id, type, desc, before, after, details}.
export async function createAuditLog(orgId, data) {
  return apiFetch('/orgs/' + orgId + '/audit', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════
// SYNC — write-through для shared.js (localStorage → сервер)
// ═══════════════════════════════════════════════════════════

// Дебаунс: не отправлять на сервер чаще чем раз в 2 секунды
let _syncMetaTimer = null;
let _syncMetaPending = null;

export async function syncMeta(meta) {
  _syncMetaPending = meta;
  if (_syncMetaTimer) return;
  _syncMetaTimer = setTimeout(async () => {
    _syncMetaTimer = null;
    const data = _syncMetaPending;
    _syncMetaPending = null;
    if (!data) return;
    try {
      await apiFetch('/sync/meta', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (err) {
      // Молча игнорируем — localStorage уже сохранён
    }
  }, 500);
}

let _syncOrgTimers = {};
let _syncOrgPending = {};
let _syncOrgCallbacks = {};

// Phase 9.1.5: onSaved(result) — опциональный коллбэк который вызывается с
// {ok, updatedAt} после успешного PUT. Несколько debounced саvе-ов могут
// схлопнуться в один PUT — все callback-и получают один и тот же result.
// Используется в App.js чтобы обновить lastKnownVersion в polling manager
// и не триггерить лишний refetch на следующем poll-цикле.
//
// Phase 10: expectedSyncedAt (4-й arg) — override для optimistic locking. App.js
// передаёт actual version из polling manager-а (он обновляется после каждого PUT/poll).
// Fallback на orgData._syncedAt если override не дан (но он stale после первого save).
// При 409 — callback получает {conflict: true, current, updatedAt}, App.js применяет
// current и показывает toast.
export async function syncOrg(orgId, orgData, onSaved, expectedSyncedAt) {
  _syncOrgPending[orgId] = orgData;
  // Сохраняем самый «свежий» expected — из последнего вызова syncOrg
  if (expectedSyncedAt) _syncOrgPending[orgId]._expectedFromCaller = expectedSyncedAt;
  if (typeof onSaved === 'function') {
    if (!_syncOrgCallbacks[orgId]) _syncOrgCallbacks[orgId] = [];
    _syncOrgCallbacks[orgId].push(onSaved);
  }
  if (_syncOrgTimers[orgId]) return;
  _syncOrgTimers[orgId] = setTimeout(async () => {
    delete _syncOrgTimers[orgId];
    const data = _syncOrgPending[orgId];
    delete _syncOrgPending[orgId];
    const callbacks = _syncOrgCallbacks[orgId] || [];
    delete _syncOrgCallbacks[orgId];
    if (!data) return;
    // Phase 10: версия из caller-а приоритетнее, fallback на orgData._syncedAt
    const expected = data._expectedFromCaller || data._syncedAt;
    delete data._expectedFromCaller;
    const payload = { ...data };
    if (expected) payload._expectedSyncedAt = expected;
    try {
      const result = await apiFetch('/sync/org/' + orgId, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      callbacks.forEach(cb => { try { cb(result); } catch (e) {} });
    } catch (err) {
      // Phase 10: 409 VERSION_CONFLICT — отдаём callback свежие данные, чтобы App.js
      // применил их и оповестил юзера. Это не «провал» — это нормальный путь race.
      if (err?.status === 409 && err?.code === 'VERSION_CONFLICT' && err?.body) {
        const conflictResult = {
          conflict: true,
          current: err.body.current,
          updatedAt: err.body.updatedAt,
        };
        callbacks.forEach(cb => { try { cb(conflictResult); } catch (e) {} });
        return;
      }
      console.warn('[API] syncOrg failed:', err.message);
    }
  }, 500);
}

// Загрузка полных данных с сервера (для начальной загрузки при наличии токена)
export async function loadMetaFromServer() {
  return withFallback(
    () => apiFetch('/sync/meta'),
    () => null
  );
}

export async function loadOrgFromServer(orgId) {
  return withFallback(
    () => apiFetch('/sync/org/' + orgId),
    () => null
  );
}

// Stage 4.0 / Phase 9: lightweight version-check для polling.
// Возвращает { updatedAt: ISOString } или null при ошибке.
// Frontend опрашивает раз в 30 сек, и только при изменении updatedAt — full fetch.
export async function getOrgVersion(orgId) {
  return withFallback(
    () => apiFetch('/sync/org/' + orgId + '/version'),
    () => null
  );
}

// ═══ EMAIL VERIFICATION (OTP) ═══

export async function sendVerificationCode(email) {
  return apiFetch('/email/send-code', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyCode(email, code) {
  return apiFetch('/email/verify-code', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}

// ═══ ПОДПИСКА И ОПЛАТА ═══

export async function getSubscriptionStatus() {
  return withFallback(
    () => apiFetch('/subscription/status'),
    () => null
  );
}

export async function calculateSubscription(orgs, months) {
  return withFallback(
    () => apiFetch(`/subscription/calculate?orgs=${orgs}&months=${months}`),
    () => null
  );
}

export async function createPayment(months, useBonuses = true) {
  return apiFetch('/payments/create', {
    method: 'POST',
    body: JSON.stringify({ months, useBonuses }),
  });
}

export async function testActivate(userId, months) {
  return apiFetch('/payments/test-activate', {
    method: 'POST',
    body: JSON.stringify({ userId, months }),
  });
}

export async function getPaymentHistory() {
  return withFallback(
    () => apiFetch('/payments/history'),
    () => []
  );
}

// ═══ НАСТРОЙКИ САЙТА (публичные) ═══

export async function getSettings() {
  return withFallback(
    () => apiFetch('/settings'),
    () => null
  );
}

// ═══ ТИКЕТЫ ПОДДЕРЖКИ ═══

export async function getMyTickets() {
  return apiFetch('/tickets');
}

export async function createTicket(subject, text, files) {
  const formData = new FormData();
  formData.append('subject', subject);
  formData.append('text', text);
  if (files) {
    for (const file of files) {
      formData.append('files', file);
    }
  }

  const url = BASE_URL + '/tickets';
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;

  const res = await fetch(url, { method: 'POST', headers, body: formData });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error || 'Ошибка создания тикета', res.status, false);
  }
  return res.json();
}

export async function getTicket(id) {
  return apiFetch('/tickets/' + id);
}

export async function replyToTicket(id, text, files) {
  const formData = new FormData();
  formData.append('text', text);
  if (files) {
    for (const file of files) {
      formData.append('files', file);
    }
  }

  const url = BASE_URL + '/tickets/' + id + '/reply';
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;

  const res = await fetch(url, { method: 'POST', headers, body: formData });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error || 'Ошибка отправки', res.status, false);
  }
  return res.json();
}

export async function getUnreadTicketCount() {
  return withFallback(
    () => apiFetch('/tickets/unread-count'),
    () => ({ count: 0 })
  );
}

// === Referral API ===
export async function getReferralInfo() {
  return apiFetch('/referral/me');
}

export async function getReferralHistory() {
  return apiFetch('/referral/history');
}

export async function searchReferralUser(query) {
  const q = encodeURIComponent(query || '');
  return apiFetch('/referral/search-user?q=' + q);
}

export async function transferBonuses(toUserId, amount, note) {
  const body = { toUserId, amount };
  if (note) body.note = note;
  return apiFetch('/referral/transfer', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ═══ Debts attachments ═══
export const uploadDebtAttachment = async (orgId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  const token = getToken();
  const r = await fetch(`${BASE_URL}/orgs/${orgId}/debts/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.error || 'Ошибка загрузки файла');
  }
  return r.json();
};

// C3 (Stage 11a, 2026-06-13): JWT больше НЕ кладётся в URL (?token=) для скачивания.
// Токен утекал в nginx access_log, историю браузера, Referer. Теперь файл грузится
// через fetch с заголовком Authorization, отдаётся как blob → open/download.
async function fetchFileObjectUrl(url) {
  const token = getToken();
  const r = await fetch(url, { headers: token ? { Authorization: 'Bearer ' + token } : {} });
  if (!r.ok) {
    if (r.status === 401) throw new Error('Сессия истекла. Обновите страницу и войдите снова.');
    throw new Error('Не удалось загрузить файл (' + r.status + ')');
  }
  const blob = await r.blob();
  return URL.createObjectURL(blob);
}

// Открыть PDF вложения долга в новой вкладке. Окно открываем СИНХРОННО по клику
// (сохраняет user-gesture → не блокируется popup-блокировщиком), затем подменяем адрес.
export const openDebtAttachment = async (orgId, debtId) => {
  const w = window.open('', '_blank');
  try {
    const blobUrl = await fetchFileObjectUrl(`${BASE_URL}/orgs/${orgId}/debts/${debtId}/attachment`);
    if (w) w.location = blobUrl; else window.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    if (w) w.close();
    throw e;
  }
};

// Скачать произвольное вложение (тикеты) по относительному/абсолютному URL.
// /uploads/... обслуживается вне префикса /api, поэтому отрезаем /api от BASE_URL.
export const downloadAuthedFile = async (url, filename) => {
  const full = url.startsWith('http') ? url : (BASE_URL.replace(/\/api$/, '') + url);
  const blobUrl = await fetchFileObjectUrl(full);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};
